import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Church, Clock, MapPin, Search, X, Cross, Calendar, Globe, 
  Sun, BookOpen, Navigation, Loader2, Map, List, SlidersHorizontal
} from "lucide-react";
import { getSacramentOptions, findSacraments, geocodeAddress } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SacramentFinderMap from "@/components/SacramentFinderMap";
import { useSEO } from "@/hooks/useSEO";

export default function SacramentFinderPage() {
  const navigate = useNavigate();
  
  // SEO
  useSEO({
    title: "Sacrament Finder - Find Mass Times Near You",
    description: "Find Catholic Mass times, Confession schedules, and Adoration hours at parishes near you. Search by location to discover your local Catholic church services.",
    path: "/sacrament-finder"
  });

  const [options, setOptions] = useState({
    liturgy_types: [],
    days: [],
    time_blocks: [],
    languages: [],
    rites: [],
    distance_options: { min: 5, max: 100, default: 25, step: 5, unit: "miles" }
  });
  const [filters, setFilters] = useState({
    sacrament_type: "mass",
    liturgy_type: null,
    day: null,
    time_block: null,
    language: null,
    rite: null
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState("list");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Location state
  const [useLocation, setUseLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationAddress, setLocationAddress] = useState("");
  const [maxDistance, setMaxDistance] = useState("25");
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const res = await getSacramentOptions();
      setOptions(res.data);
      if (res.data.distance_options?.default) {
        setMaxDistance(String(res.data.distance_options.default));
      }
    } catch (err) {
      toast.error("Failed to load filter options");
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'gps'
        });
        setLocationAddress("Current Location");
        setUseLocation(true);
        setGettingLocation(false);
        toast.success("Location detected!");
      },
      (error) => {
        setGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location permission denied. Please enter an address instead.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("Unable to get your location.");
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleGeocodeAddress = async () => {
    if (!locationAddress.trim() || locationAddress === "Current Location") {
      toast.error("Please enter an address or zip code");
      return;
    }
    
    setGeocoding(true);
    try {
      const res = await geocodeAddress(locationAddress.trim());
      setUserLocation({ 
        lat: res.data.lat, 
        lng: res.data.lng,
        source: 'geocode',
        display_name: res.data.display_name || locationAddress
      });
      setUseLocation(true);
      toast.success("Address found!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not find that address. Try a more specific address or zip code.");
    }
    setGeocoding(false);
  };

  const clearLocation = () => {
    setUseLocation(false);
    setUserLocation(null);
    setLocationAddress("");
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    // Require location
    if (!userLocation && !locationAddress.trim()) {
      toast.error("Please enter a location or use your current location");
      return;
    }
    
    setLoading(true);
    setSearched(true);
    try {
      const searchFilters = { ...filters };
      Object.keys(searchFilters).forEach(key => {
        if (searchFilters[key] === null || searchFilters[key] === "all") {
          delete searchFilters[key];
        }
      });
      
      let currentLocation = userLocation;
      
      if (locationAddress.trim() && locationAddress !== "Current Location" && !userLocation) {
        try {
          const res = await geocodeAddress(locationAddress.trim());
          currentLocation = { lat: res.data.lat, lng: res.data.lng, source: 'geocode', display_name: res.data.display_name || locationAddress };
          setUserLocation(currentLocation);
          setUseLocation(true);
        } catch {
          toast.error("Could not find that location. Try a different address or zip code.");
          setLoading(false);
          return;
        }
      }
      
      if (currentLocation) {
        searchFilters.user_lat = currentLocation.lat;
        searchFilters.user_lng = currentLocation.lng;
        searchFilters.max_distance = parseInt(maxDistance);
      }
      
      const res = await findSacraments(searchFilters);
      setResults(res.data.results);
    } catch {
      toast.error("Search failed");
    }
    setLoading(false);
  };

  const clearFilters = () => {
    setFilters({
      sacrament_type: filters.sacrament_type,
      liturgy_type: null,
      day: null,
      time_block: null,
      language: null,
      rite: null
    });
    clearLocation();
    setSearched(false);
    setResults([]);
  };

  const advancedFiltersCount = [filters.liturgy_type, filters.language, filters.rite].filter(Boolean).length;

  const getSacramentLabel = () => {
    switch (filters.sacrament_type) {
      case "mass": return "Mass Times";
      case "reconciliation": return "Confession Times";
      case "adoration": return "Adoration Times";
      default: return "Results";
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="sacrament-finder-page">
      
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]">
              Sacrament Finder
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              Find Mass, Reconciliation, and Adoration times at parishes near you
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center bg-white rounded-lg border border-[#E5E0D5] p-1">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className={`font-['Nunito'] text-xs ${viewMode === "list" ? 'bg-[var(--liturgical-primary)] text-white' : 'text-[#6B665E]'}`}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === "map" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("map")}
              className={`font-['Nunito'] text-xs ${viewMode === "map" ? 'bg-[var(--liturgical-primary)] text-white' : 'text-[#6B665E]'}`}
              data-testid="map-view-btn"
            >
              <Map className="w-4 h-4 mr-1" />
              Map
            </Button>
          </div>
        </div>

        {/* Sacrament Type Tabs */}
        <Tabs 
          value={filters.sacrament_type} 
          onValueChange={(v) => {
            setFilters({ ...filters, sacrament_type: v, liturgy_type: null });
            setSearched(false);
            setResults([]);
          }}
          className="mb-6"
        >
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5] w-full grid grid-cols-3">
            <TabsTrigger 
              value="mass" 
              className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="tab-mass"
            >
              <Church className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Mass</span>
              <span className="sm:hidden">Mass</span>
            </TabsTrigger>
            <TabsTrigger 
              value="reconciliation" 
              className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="tab-reconciliation"
            >
              <Cross className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Confession</span>
              <span className="sm:hidden">Conf.</span>
            </TabsTrigger>
            <TabsTrigger 
              value="adoration" 
              className="font-['Nunito'] text-xs sm:text-sm data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="tab-adoration"
            >
              <Sun className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Adoration</span>
              <span className="sm:hidden">Ador.</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search and Filters Card */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="space-y-3">
              {/* Row 1: Location (prominent) */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div className="flex-1 w-full">
                  <label className="text-xs font-medium text-[#2D2A26] font-['Nunito'] mb-1 block">Location *</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                      <Input 
                        value={locationAddress === "Current Location" ? "Current Location" : locationAddress}
                        onChange={e => { setLocationAddress(e.target.value); if (userLocation) { setUserLocation(null); setUseLocation(false); } }}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleGeocodeAddress())}
                        placeholder="Enter zip code, city, or address..."
                        className="pl-10 border-[#E5E0D5] font-['Nunito'] text-sm h-10"
                        data-testid="location-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGeocodeAddress}
                      disabled={geocoding || !locationAddress || locationAddress === "Current Location"}
                      className="font-['Nunito'] text-xs h-10 border-[#E5E0D5] px-3"
                      data-testid="geocode-btn"
                    >
                      {geocoding ? <Loader2 className="w-3 h-3 animate-spin" /> : "Find"}
                    </Button>
                    <span className="text-xs text-[#6B665E] font-['Nunito'] hidden sm:inline">or</span>
                    <Button
                      type="button"
                      variant={userLocation?.source === 'gps' ? "default" : "outline"}
                      size="sm"
                      onClick={handleGetCurrentLocation}
                      disabled={gettingLocation}
                      className={`font-['Nunito'] text-xs h-10 ${userLocation?.source === 'gps' ? 'bg-[var(--liturgical-primary)]' : 'border-[#E5E0D5]'}`}
                      data-testid="use-location-btn"
                    >
                      {gettingLocation ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Navigation className="w-3 h-3 mr-1" />}
                      <span className="hidden sm:inline">Use My Location</span>
                      <span className="sm:hidden">GPS</span>
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={maxDistance} onValueChange={setMaxDistance}>
                    <SelectTrigger className="w-32 border-[#E5E0D5] font-['Nunito'] text-sm h-10">
                      <SelectValue placeholder="Distance" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="5" className="font-['Nunito']">Within 5 mi</SelectItem>
                      <SelectItem value="10" className="font-['Nunito']">Within 10 mi</SelectItem>
                      <SelectItem value="25" className="font-['Nunito']">Within 25 mi</SelectItem>
                      <SelectItem value="50" className="font-['Nunito']">Within 50 mi</SelectItem>
                      <SelectItem value="100" className="font-['Nunito']">Within 100 mi</SelectItem>
                    </SelectContent>
                  </Select>
                  {userLocation && (
                    <Button type="button" variant="ghost" size="sm" onClick={clearLocation}
                      className="font-['Nunito'] text-xs h-10 text-red-600 hover:text-red-700 px-2"><X className="w-3 h-3" /></Button>
                  )}
                </div>
              </div>

              {/* Location indicator */}
              {userLocation && (
                <Badge className="bg-green-100 text-green-700 font-['Nunito'] text-xs">
                  <MapPin className="w-3 h-3 mr-1" />
                  {userLocation.source === 'gps' ? 'Using GPS' : (userLocation.display_name || locationAddress)}
                </Badge>
              )}

              {/* Row 2: Day, Time, More Filters, Search */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-[#E5E0D5]">
                <Select 
                  value={filters.day || "all"} 
                  onValueChange={(v) => setFilters({ ...filters, day: v === "all" ? null : v })}
                >
                  <SelectTrigger className="w-full sm:w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="filter-day">
                    <SelectValue placeholder="Any Day" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E5E0D5]">
                    <SelectItem value="all" className="font-['Nunito'] text-sm">Any Day</SelectItem>
                    {options.days.map(day => (
                      <SelectItem key={day} value={day} className="font-['Nunito'] text-sm">{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.time_block || "all"} 
                  onValueChange={(v) => setFilters({ ...filters, time_block: v === "all" ? null : v })}
                >
                  <SelectTrigger className="w-full sm:w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="filter-time">
                    <SelectValue placeholder="Any Time" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E5E0D5]">
                    <SelectItem value="all" className="font-['Nunito'] text-sm">Any Time</SelectItem>
                    {options.time_blocks.map(block => (
                      <SelectItem key={block.key} value={block.key} className="font-['Nunito'] text-sm">{block.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`border-[#E5E0D5] font-['Nunito'] text-sm ${showAdvancedFilters ? 'bg-[var(--liturgical-primary)]/10 border-[var(--liturgical-primary)]' : ''}`}
                  data-testid="advanced-filters-btn"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  More Filters
                  {advancedFiltersCount > 0 && (
                    <Badge className="ml-2 bg-[var(--liturgical-primary)] text-white text-[10px] px-1.5">
                      {advancedFiltersCount}
                    </Badge>
                  )}
                </Button>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] sm:ml-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>

              {/* Advanced Filters (Type, Language, Rite only) */}
              {showAdvancedFilters && (
                <div className="pt-3 border-t border-[#E5E0D5]">
                  <div className="flex flex-wrap items-center gap-3">
                    {filters.sacrament_type === "mass" && (
                      <Select 
                        value={filters.liturgy_type || "all"} 
                        onValueChange={(v) => setFilters({ ...filters, liturgy_type: v === "all" ? null : v })}
                      >
                        <SelectTrigger className="w-full sm:w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="filter-liturgy">
                          <SelectValue placeholder="Mass Type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#E5E0D5]">
                          <SelectItem value="all" className="font-['Nunito'] text-sm">All Types</SelectItem>
                          {options.liturgy_types.map(type => (
                            <SelectItem key={type} value={type} className="font-['Nunito'] text-sm">{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {filters.sacrament_type === "mass" && (
                      <Select 
                        value={filters.language || "all"} 
                        onValueChange={(v) => setFilters({ ...filters, language: v === "all" ? null : v })}
                      >
                        <SelectTrigger className="w-full sm:w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="filter-language">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#E5E0D5]">
                          <SelectItem value="all" className="font-['Nunito'] text-sm">Any Language</SelectItem>
                          {options.languages.map(lang => (
                            <SelectItem key={lang} value={lang} className="font-['Nunito'] text-sm">{lang}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <Select 
                      value={filters.rite || "all"} 
                      onValueChange={(v) => setFilters({ ...filters, rite: v === "all" ? null : v })}
                    >
                      <SelectTrigger className="w-full sm:w-40 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="filter-rite">
                        <SelectValue placeholder="Rite" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E5E0D5]">
                        <SelectItem value="all" className="font-['Nunito'] text-sm">Any Rite</SelectItem>
                        {options.rites && options.rites.map(rite => (
                          <SelectItem key={rite} value={rite} className="font-['Nunito'] text-sm">{rite}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {advancedFiltersCount > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="font-['Nunito'] text-xs text-[var(--liturgical-primary)] h-9"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Results Section */}
        {searched && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">
                {results.length} {getSacramentLabel()} Found
              </h2>
            </div>

            {/* Map View */}
            {viewMode === "map" && results.length > 0 && (
              <div className="rounded-lg overflow-hidden border border-[#E5E0D5]">
                <SacramentFinderMap 
                  userLocation={userLocation}
                  results={results}
                  locationAddress={locationAddress}
                />
              </div>
            )}

            {/* List View */}
            {viewMode === "list" && (
              <>
                {results.length === 0 ? (
                  <Card className="bg-white border-[#E5E0D5]">
                    <CardContent className="p-8 text-center">
                      {filters.sacrament_type === "adoration" ? (
                        <Sun className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                      ) : (
                        <Church className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                      )}
                      <p className="text-sm text-[#6B665E] font-['Nunito']">
                        No {getSacramentLabel().toLowerCase()} match your filters.
                      </p>
                      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
                        Try adjusting your search criteria.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {results.map((result, idx) => (
                      <Card 
                        key={`${result.parish_id}-${idx}`} 
                        className="bg-white border-[#E5E0D5] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/parish/${result.parish_id}`)}
                        data-testid={`result-${idx}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex gap-3 sm:gap-4">
                            {/* Parish Image */}
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-md overflow-hidden flex-shrink-0 bg-[#F0EBE0]">
                              {result.parish_image ? (
                                <img 
                                  src={result.parish_image} 
                                  alt={result.parish_name} 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Church className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--liturgical-primary)]" />
                                </div>
                              )}
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-['Crimson_Text'] text-base sm:text-lg font-semibold text-[#2D2A26] truncate">
                                {result.parish_name}
                              </h3>
                              <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{result.parish_address || result.parish_location}</span>
                                {result.distance_formatted && (
                                  <Badge className="bg-[var(--liturgical-primary)] text-white text-[10px] font-['Nunito'] ml-1 flex-shrink-0">
                                    {result.distance_formatted}
                                  </Badge>
                                )}
                              </p>

                              {/* Schedule Info */}
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2">
                                <Badge variant="outline" className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] text-[10px] font-['Nunito']">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {result.day}
                                </Badge>
                                <Badge variant="outline" className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] text-[10px] font-['Nunito']">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {result.sacrament_type === "mass" 
                                    ? result.time 
                                    : `${result.start_time} - ${result.end_time}`
                                  }
                                </Badge>
                                {result.perpetual && (
                                  <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border-[var(--liturgical-secondary)] text-[10px] font-['Nunito']">
                                    Perpetual
                                  </Badge>
                                )}
                                {result.liturgy_type && (
                                  <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] border-[var(--liturgical-secondary)] text-[10px] font-['Nunito'] hidden sm:flex">
                                    {result.liturgy_type}
                                  </Badge>
                                )}
                                {result.parish_rite && (
                                  <Badge className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border-[var(--liturgical-primary)] text-[10px] font-['Nunito'] hidden sm:flex">
                                    <BookOpen className="w-3 h-3 mr-1" />
                                    {result.parish_rite}
                                  </Badge>
                                )}
                                {result.language && result.language !== "English" && (
                                  <Badge variant="outline" className="border-[#6B665E] text-[#6B665E] text-[10px] font-['Nunito']">
                                    <Globe className="w-3 h-3 mr-1" />
                                    {result.language}
                                  </Badge>
                                )}
                              </div>

                              {result.notes && (
                                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1.5 italic line-clamp-1">
                                  {result.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Initial State */}
        {!searched && (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F0EBE0] mb-4">
                <Search className="w-8 h-8 text-[#6B665E]" />
              </div>
              <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-2">
                Ready to Search
              </h3>
              <p className="text-sm text-[#6B665E] font-['Nunito'] max-w-sm mx-auto">
                Enter your location above to find {filters.sacrament_type === "mass" ? "Mass" : filters.sacrament_type === "reconciliation" ? "Confession" : "Adoration"} times 
                at parishes near you.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
